import React, { useEffect, useState } from "react";

import HeaderWithIcon from "../../components/common/HeaderWithIcon";
import WithDrawRequestTabs from "./WithDrawRequestTabs";
import { withdrawalsApi } from "../../api/withdrawals";
import { useAuth } from "../../context/AuthContext";

export default function WithDrawRequest() {
  const { tokens } = useAuth();
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ 
    page: 1, 
    limit: 20, 
    total: 0, 
    status: "all"
  });
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [flaggedOnly, setFlaggedOnly] = useState(false);

  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        status: pagination.status === 'all' ? '' : pagination.status
      };

      // Add search query if present
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      // Add flagged filter if present
      if (flaggedOnly) {
        params.flagged = true;
      }

      // Add date range if present
      if (dateRange.startDate) {
        params.startDate = dateRange.startDate.toISOString();
      }
      if (dateRange.endDate) {
        params.endDate = dateRange.endDate.toISOString();
      }

      const { data } = await withdrawalsApi.getWithdrawals(tokens.access.token, params);
      setWithdrawals(data.rows);
      setPagination(prev => ({ ...prev, total: data.count }));
    } catch (error) {
      console.error("Failed to fetch withdrawals:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawals();
  }, [pagination.page, pagination.limit, pagination.status, searchQuery, dateRange, flaggedOnly]); // Add search and date to dependencies

  const handleSearch = (query) => {
    setSearchQuery(query);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setDateRange({ startDate: null, endDate: null });
    setFlaggedOnly(false);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleFlaggedOnlyChange = (value) => {
    setFlaggedOnly(value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  // Updated tabs structure
  const tabsData = [
    {
      title: "Downline",
      cards: [
        { title: "All", status: "all" },
        { title: "Pending", status: "pending" },
        { title: "Approved", status: "approved" },
        { title: "Declined", status: "declined" } // Changed from 'rejected'
      ]
    }
  ];

  const handleStatusChange = (status) => {
    setPagination(prev => ({
      ...prev,
      page: 1, // Reset to first page when changing status
      status: status
    }));
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({
      ...prev,
      page: page
    }));
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="min-h-screen relative overflow-hidden font-poppins">
      <HeaderWithIcon title="Withdrawal Request" />
      <WithDrawRequestTabs 
        tabsData={tabsData} 
        withdrawals={withdrawals}
        loading={loading}
        onStatusChange={fetchWithdrawals}
        currentStatus={pagination.status}
        handleStatusChange={handleStatusChange}
        pagination={{
          currentPage: pagination.page,
          totalPages: totalPages,
          total: pagination.total,
          onPageChange: handlePageChange
        }}
        searchQuery={searchQuery}
        onSearch={handleSearch}
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        onClearFilters={handleClearFilters}
        flaggedOnly={flaggedOnly}
        onFlaggedOnlyChange={handleFlaggedOnlyChange}
      />
    </div>
  );
}
